import { createContext, useContext } from "react";

import { toaster } from "@/web/components/primitives/toast";

/**
 * The write seam for the admin SMB viewer.
 *
 * All SpacetimeDB writes on the frontend funnel through `useReducerMutation` /
 * `useProcedureMutation` in {@link file://./tanstack.ts}. Those hooks consult
 * this context: when a subtree is wrapped in {@link ReadOnlyMutationsProvider},
 * every `mutate` / `mutateAsync` is neutralized (no server call, a toast
 * instead) so an admin impersonating a business can look but never touch.
 *
 * Default is `false`, so the entire normal app is unaffected — the guard only
 * engages under the provider mounted by `/admin/smb/$brandId`.
 *
 * **Extending the guard:** whatever it substitutes must stay as referentially
 * stable as what react-query returns, because callers put `mutate` /
 * `mutateAsync` in dependency arrays on that assumption. Change what the
 * callables *do*, never how often their *identity* changes. A defect here shows
 * up only in the admin viewer — the least-exercised path in the app — and the
 * first version of this guard cost an infinite render loop there. See
 * `docs/patterns/web/architecture-primitives/reducer-procedure-hooks.md`
 * ("Read-only subtrees").
 */
const ReadOnlyMutationsContext = createContext<boolean>(false);

export function ReadOnlyMutationsProvider({ children }: { children: React.ReactNode }) {
  return <ReadOnlyMutationsContext value={true}>{children}</ReadOnlyMutationsContext>;
}

export function useIsReadOnly(): boolean {
  return useContext(ReadOnlyMutationsContext);
}

/** Error a guarded `mutateAsync` rejects with, so awaiting flows short-circuit. */
export class ReadOnlyMutationError extends Error {
  constructor() {
    super("Read-only admin view — changes are disabled.");
    this.name = "ReadOnlyMutationError";
  }
}

let lastReadOnlyToastAt = 0;

/**
 * Fires the "read-only" toast, throttled so a component that fires several
 * blocked mutations in quick succession doesn't stack duplicate toasts.
 */
export function notifyReadOnlyBlocked(): void {
  const now = Date.now();
  if (now - lastReadOnlyToastAt < 1500) return;
  lastReadOnlyToastAt = now;
  toaster({ message: "Read-only admin view — changes are disabled.", variant: "dark" });
}
